import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/* ─────────────────────────────────────────────────────────────────────────
   L17.08 — Master Config Dashboard (`/admin/master-config`, §35 #1).
   Landing page for the M17 master-configuration domain: headline counts plus
   navigation tiles to every M17 surface.
   ───────────────────────────────────────────────────────────────────────── */

interface Counts {
  // null means "this reader is not allowed to see it" — never shown as 0,
  // because a count the server refused is not a count of none.
  readonly rateCards: number | null;
  readonly pendingApprovals: number | null;
}

const TILES: readonly { readonly to: string; readonly label: string; readonly key: string }[] = [
  { to: '/admin/rate-master/cards', label: 'Rate Master', key: 'm17.masterDashboard.tileRateMaster' },
  { to: '/admin/master-config/engine', label: 'Configuration Engine', key: 'm17.masterDashboard.tileConfigEngine' },
  { to: '/admin/config-extended', label: 'Extended Configuration', key: 'm17.masterDashboard.tileExtendedConfig' },
  { to: '/admin/master-config/territories', label: 'Territory Master', key: 'm17.masterDashboard.tileTerritoryMaster' },
  { to: '/admin/master-config/bulk', label: 'Bulk Import & Data Quality', key: 'm17.masterDashboard.tileBulkImport' },
  { to: '/admin/master-config/versions', label: 'Version History', key: 'm17.masterDashboard.tileVersionHistory' },
  { to: '/admin/rate-master/snapshot-lookup', label: 'Snapshot Lookup', key: 'm17.masterDashboard.tileSnapshotLookup' },
];

export function M17MasterDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [counts, setCounts] = useState<Counts | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // A refusal and a failure are different answers and must not be merged.
    // 403 means the count is not this reader's to see, so it is reported as
    // unknown; anything else is a real failure and is shown as one.
    const countOf = async (path: string): Promise<number | null> => {
      try {
        const r = await apiRequest<{ data: unknown[] }>(path, { token });
        return r.data?.length ?? 0;
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) return null;
        throw e;
      }
    };
    Promise.all([countOf('/v1/m17/rates'), countOf('/v1/m17/governance/approvals')])
      .then(([rateCards, pendingApprovals]) => {
        if (alive) { setCounts({ rateCards, pendingApprovals }); setError(null); }
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const shown = (v: number | null | undefined): string =>
    v == null ? '—' : String(v);

  return (
    <main aria-label="master-config-dashboard">
      <h1>{tr('m17.masterDashboard.title')}</h1>
      {error && <p role="alert">{error}</p>}
      <section aria-label="master-config-metrics">
        <p>{tr('m17.masterDashboard.rateCards')} <strong>{counts ? shown(counts.rateCards) : '…'}</strong></p>
        <p>{tr('m17.masterDashboard.pendingApprovals')} <strong>{counts ? shown(counts.pendingApprovals) : '…'}</strong></p>
      </section>
      <nav aria-label="master-config-nav">
        <ul>
          {TILES.map((t) => (
            <li key={t.to}><Link to={t.to} aria-label={`tile-${t.label}`}>{tr(t.key)}</Link></li>
          ))}
        </ul>
      </nav>
    </main>
  );
}
